// components/crm/ContactDetail.tsx
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Card,
  Button,
  Spin,
  Tabs,
  Form,
  Input,
  Select,
  Divider,
  Space,
  Typography,
  Table,
  Modal,
  DatePicker,
  Popconfirm,
  message,
  Row,
  Col,
  Tooltip,
} from 'antd';
import {
  EditOutlined,
  SaveOutlined,
  DeleteOutlined,
  PlusOutlined,
  ArrowLeftOutlined,
  PlusCircleOutlined,
  MinusCircleOutlined,
  GoogleOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { useStore } from '../../stores/root-store-context';
import { Contact, CrmEvent } from '../../api/CRMApiClient';
import { ContactValidationKind } from '../../enums/ContactValidationKind';
import ERPLayout from '../../components/layout/ERPLayout';
import DuplicateWarning from '../../components/crm/DuplicateWarning';
import CrmAuditInfo from '../../components/crm/CrmAuditInfo';
import CrmHistoryTab from '../../components/crm/CrmHistoryTab';
import CrmAttachmentsTab from '../../components/crm/CrmAttachmentsTab';
import FormCheckboxDropdown from '../../components/crm/FormCheckboxDropdown';
import {
  findSimilarContacts,
  findDuplicateContactEmail,
  findDuplicateContactPhone,
  checkAdditionalInfoDuplicate,
  isValidPhoneNumber,
  normalizeDigits,
} from '../../helpers/crmDeduplication';
import { useGoogleLogin } from '@react-oauth/google';
import { formatGoogleError } from '../../helpers/googleErrorFormatter';
import { GoogleSyncToast, GoogleSyncToastState } from '../../components/crm/GoogleSyncToast';

// Add dayjs plugins
dayjs.extend(utc);
dayjs.extend(timezone);

const toLocal = (iso: string) => dayjs(iso).local();
const toApi = (d: Dayjs) => d.utc().format("YYYY-MM-DDTHH:mm:ss");

const { TabPane } = Tabs;
const { Option } = Select;
const { Title, Text } = Typography;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

const ContactDetail: React.FC = observer(() => {
  const { t } = useTranslation();
  const { contactId } = useParams<{ contactId?: string }>();
  const navigate = useNavigate();
  const { crmStore, applicationStore } = useStore();

  /* ─────────────────────── mode helpers ─────────────────────── */

  const isCreateMode = !contactId || contactId === 'new';
  const contactIdNumber = useMemo(
    () => (!isCreateMode && !isNaN(Number(contactId)) ? Number(contactId) : null),
    [isCreateMode, contactId],
  );

  /* ─────────────────────── state & forms ────────────────────── */

  const [form] = Form.useForm<Contact>();
  const [searchParams] = useSearchParams();
  const [editing, setEditing] = useState<boolean>(isCreateMode || searchParams.get('edit') === '1');
  const [loading, setLoading] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState<string>('details');

  const [eventModalVisible, setEventModalVisible] = useState<boolean>(false);
  const [eventForm] = Form.useForm();
  const [editingEvent, setEditingEvent] = useState<CrmEvent | null>(null);
  const [relatedEvents, setRelatedEvents] = useState<CrmEvent[]>([]);

  // ─────────── Real-time duplicate & similarity checks ───────────
  const watchedContactName = Form.useWatch('name', form);
  const watchedContactSurname = Form.useWatch('surname', form);
  const watchedContactEmail = Form.useWatch('email', form);
  const watchedContactPhone = Form.useWatch('phoneNumber', form);
  const watchedContactAdditionalInfos = Form.useWatch('additionalInfos', form);

  const tenant = applicationStore.company?.Tenant;
  const tenantPath = tenant ? `/${tenant}` : '';

  const contactNameDuplicates = useMemo(() => {
    if (!watchedContactName && !watchedContactSurname) return null;
    return findSimilarContacts(
      watchedContactName || '',
      watchedContactSurname || '',
      crmStore.contacts,
      contactIdNumber,
    );
  }, [watchedContactName, watchedContactSurname, crmStore.contacts, contactIdNumber]);

  const contactEmailDuplicate = useMemo(() => {
    if (!watchedContactEmail) return null;
    return findDuplicateContactEmail(watchedContactEmail, crmStore.contacts, contactIdNumber);
  }, [watchedContactEmail, crmStore.contacts, contactIdNumber]);

  const contactPhoneDuplicate = useMemo(() => {
    if (!watchedContactPhone) return null;
    return findDuplicateContactPhone(watchedContactPhone, crmStore.contacts, contactIdNumber);
  }, [watchedContactPhone, crmStore.contacts, contactIdNumber]);

  /* ─────────────────────── Google Contacts sync ─────────────────────── */
  const isGoogleContactsEnabled =
    applicationStore.company?.EnableGoogleContactsSync ||
    applicationStore.company?.EnableGoogleSync ||
    true;

  const currentPhone = watchedContactPhone !== undefined ? watchedContactPhone : crmStore.currentContact?.phoneNumber;
  const isPhoneValidForGoogle = Boolean(currentPhone) && isValidPhoneNumber(currentPhone) && normalizeDigits(currentPhone).length >= 9;

  const [cachedGoogleToken, setCachedGoogleToken] = useState<string | null>(() => {
    return sessionStorage.getItem('google_contacts_access_token');
  });

  const [syncToast, setSyncToast] = useState<GoogleSyncToastState>({
    visible: false,
    status: 'syncing',
  });

  const [isSyncingSingleGoogle, setIsSyncingSingleGoogle] = useState<boolean>(false);

  const triggerGoogleOAuthSync = useGoogleLogin({
    flow: 'auth-code',
    scope: 'https://www.googleapis.com/auth/contacts https://www.googleapis.com/auth/userinfo.email',
    onSuccess: async (codeResponse) => {
      console.log('[GoogleContacts] Received OAuth auth code from Google:', codeResponse);
      try {
        const connRes = await crmStore.connectGoogleContacts(codeResponse.code);
        if (connRes && 'connected' in connRes && connRes.connected) {
          await applicationStore.updateCompany();
        }
      } catch (e) {
        console.warn('[GoogleContacts] Failed to persist Google connection:', e);
      }

      if (!contactIdNumber) return;
      setIsSyncingSingleGoogle(true);
      setSyncToast({
        visible: true,
        status: 'syncing',
        totalCount: 1,
      });
      try {
        const res = await crmStore.syncSingleContactToGoogle(contactIdNumber);
        if (res && res.resourceName && !res.error) {
          setSyncToast({
            visible: true,
            status: 'success',
            syncedCount: 1,
          });
        } else {
          const errMsg = formatGoogleError(res?.error || 'Failed to sync contact', t);
          setSyncToast({
            visible: true,
            status: 'error',
            errorMessage: errMsg,
          });
        }
      } catch (err: any) {
        const errMsg = formatGoogleError(err, t);
        setSyncToast({
          visible: true,
          status: 'error',
          errorMessage: errMsg,
        });
      } finally {
        setIsSyncingSingleGoogle(false);
      }
    },
    onError: (error) => {
      console.error('[GoogleContacts] Google OAuth popup error:', error);
      setIsSyncingSingleGoogle(false);
      setSyncToast({
        visible: true,
        status: 'error',
        errorMessage: t('googleErrors.syncFailed', 'Failed to connect Google account.'),
      });
    },
  });

  const handleSyncThisContact = async () => {
    if (!contactIdNumber || crmStore.isGoogleSyncing || isSyncingSingleGoogle) return;
    setIsSyncingSingleGoogle(true);

    setSyncToast({
      visible: true,
      status: 'syncing',
      totalCount: 1,
    });

    try {
      const res = await crmStore.syncSingleContactToGoogle(contactIdNumber, cachedGoogleToken || undefined);
      if (res && res.resourceName && !res.error) {
        setSyncToast({
          visible: true,
          status: 'success',
          syncedCount: 1,
        });
        setIsSyncingSingleGoogle(false);
        return;
      }

      console.warn('[GoogleContacts] Direct single contact sync failed:', res?.error);
      const errLower = (res?.error || '').toLowerCase();
      const isAuthExpired =
        errLower.includes('401') ||
        errLower.includes('unauthorized') ||
        errLower.includes('unauthenticated') ||
        errLower.includes('invalid authentication credentials') ||
        errLower.includes('invalid credential') ||
        errLower.includes('invalid_token') ||
        errLower.includes('invalid_grant') ||
        errLower.includes('expired') ||
        errLower.includes('err_google_unauthorized_client') ||
        errLower.includes('err_google_credentials_missing') ||
        errLower.includes('err_google_scope_insufficient');

      if (isAuthExpired) {
        sessionStorage.removeItem('google_contacts_access_token');
        setCachedGoogleToken(null);
        triggerGoogleOAuthSync();
        return;
      }

      const errMsg = formatGoogleError(res?.error, t);
      setSyncToast({
        visible: true,
        status: 'error',
        errorMessage: errMsg,
      });
    } catch (err: any) {
      sessionStorage.removeItem('google_contacts_access_token');
      setCachedGoogleToken(null);
      const errMsg = formatGoogleError(err, t);
      setSyncToast({
        visible: true,
        status: 'error',
        errorMessage: errMsg,
      });
    } finally {
      setIsSyncingSingleGoogle(false);
    }
  };

  /* ─────────────────────── helper: build smart event name ─────────────────────── */
  const buildCompositeName = useCallback(
    (companyIds?: number[], contactIds?: number[], range?: [Dayjs, Dayjs]) => {
      const parts: string[] = [];

      if (companyIds?.length) {
        const first = crmStore.externalCompanies.find(c => c.id === companyIds[0]);
        if (first) parts.push(first.name);
        if (companyIds.length > 1) parts.push(`+${companyIds.length - 1}`);
      }

      if (contactIds?.length) {
        const first = crmStore.contacts.find(c => c.id === contactIds[0]);
        if (first) parts.push(`${first.name} ${first.surname}`.trim());
        if (contactIds.length > 1) parts.push(`+${contactIds.length - 1}`);
      }

      if (range?.[0]) parts.push(range[0].format("DD/MM/YYYY"));
      return parts.filter(Boolean).join(" — ");
    },
    [crmStore.externalCompanies, crmStore.contacts],
  );

  /* ─────────────────────── data loading ─────────────────────── */

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        await Promise.all([
          crmStore.getClientRoles(),
          crmStore.getExternalCompanies(),
          crmStore.getCrmEventTypes(),
          crmStore.getContactTypes(),
          crmStore.getContactsWithDetails(),
        ]);

        if (!isCreateMode && contactIdNumber !== null) {
          const contact = await crmStore.getContactById(contactIdNumber);
          if (contact) {
            form.setFieldsValue({
              ...contact,
              additionalInfos: contact.additionalInfos || [],
            });

            const events = await crmStore.ApiStore.CRMApiClient.getCrmEventsByContact(
              contact.id,
            );
            setRelatedEvents(events);
          }
        } else {
          form.setFieldsValue({
            name: '',
            surname: '',
            email: '',
            phoneNumber: '',
            additionalInfos: [],
          });
        }
      } finally {
        setLoading(false);
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreateMode, contactIdNumber]);

  const contactTypeOptions = useMemo(() => {
    const dbTypes = crmStore.contactTypes.map(ct => ct.name).filter(Boolean);
    const defaultTypes = ['Email', 'Phone', 'Telegram', 'Skype', 'LinkedIn'];
    const baseList = dbTypes.length > 0 ? dbTypes : defaultTypes;
    const result: string[] = [];
    const seen = new Set<string>();
    for (const name of baseList) {
      const lower = name.toLowerCase().trim();
      if (!seen.has(lower)) {
        seen.add(lower);
        result.push(name);
      }
    }
    return result;
  }, [crmStore.contactTypes]);

  const hasExactDuplicate = Boolean(
    contactPhoneDuplicate ||
    contactEmailDuplicate ||
    contactNameDuplicates?.exactMatch
  );

  /* ─────────────────────── contact save ─────────────────────── */

  const onSave = async () => {
    try {
      const values = await form.validateFields();

      if (contactPhoneDuplicate) {
        message.error(
          `${t('ContactWithPhoneExists', 'Контакт с таким номером телефона уже существует')}: ${contactPhoneDuplicate.name} ${contactPhoneDuplicate.surname}`
        );
        return;
      }

      if (contactEmailDuplicate) {
        message.error(
          `${t('ContactWithEmailExists', 'Контакт с таким email уже существует')}: ${contactEmailDuplicate.name} ${contactEmailDuplicate.surname}`
        );
        return;
      }

      if (contactNameDuplicates?.exactMatch) {
        message.error(
          `${t('ContactWithNameExists', 'Контакт с таким именем и фамилией уже существует')}: ${contactNameDuplicates.exactMatch.name} ${contactNameDuplicates.exactMatch.surname}`
        );
        return;
      }

      if (isCreateMode) {
        const created = await crmStore.addContact(values);
        if (created?.id) {
          message.success(t('CreatedSuccessfully'));
          navigate(`/${applicationStore.company?.Tenant}/crm/contact/${created.id}`);
        } else if (created) {
          message.success(t('CreatedSuccessfully'));
          navigate(`/${applicationStore.company?.Tenant}/crm/contacts`);
        } else {
          message.error(crmStore.error || t('SomethingWentWrong'));
        }
      } else if (contactIdNumber !== null) {
        const ok = await crmStore.updateContact(contactIdNumber, values);
        if (ok !== false) {
          const updated = await crmStore.getContactById(contactIdNumber);
          if (updated) {
            form.setFieldsValue({
              ...updated,
              additionalInfos: updated.additionalInfos || [],
            });
          }
          message.success(t('SavedSuccessfully'));
          setEditing(false);
        } else {
          message.error(crmStore.error || t('SomethingWentWrong'));
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    if (crmStore.currentContact) {
      form.setFieldsValue({
        ...crmStore.currentContact,
        additionalInfos: crmStore.currentContact.additionalInfos || [],
      });
    }
    setEditing(false);
  };

  const onDelete = async () => {
    if (contactIdNumber === null) return;
    await crmStore.deleteContact(contactIdNumber);
    navigate(`/${applicationStore.company?.Tenant}/crm/contacts`);
  };

  /* ─────────────────────── events helpers ───────────────────── */

  const refreshEvents = async () => {
    if (contactIdNumber === null) return;
    const events = await crmStore.ApiStore.CRMApiClient.getCrmEventsByContact(
      contactIdNumber,
    );
    setRelatedEvents(events);
  };

  const openAddEvent = () => {
    if (contactIdNumber === null) return;
    setEditingEvent(null);
    const now = dayjs();
    eventForm.setFieldsValue({
      dateRange: [now, now.clone().add(30, 'minutes')],
      contactIds: [contactIdNumber], // Use multiple contact support
      externalCompanyIds: [],
      name: '',
      description: '',
      typeId: null,
    });
    setEventModalVisible(true);
  };

  const openEditEvent = (event: CrmEvent) => {
    setEditingEvent(event);
    const dateRange: [Dayjs, Dayjs] = [
      toLocal(event.dateStart), 
      toLocal(event.dateEnd)
    ];
    
    // Map the event data properly for the full form
    const formData = {
      name: event.name,
      description: event.description,
      typeId: event.typeId,
      dateRange,
      externalCompanyIds: event.externalCompanies?.map(c => c.id) ?? [],
      contactIds: event.contacts?.map(c => c.id) ?? [],
    };
    
    eventForm.setFieldsValue(formData);
    setEventModalVisible(true);
  };

  const selectedEventCompanyIds: number[] = Form.useWatch('externalCompanyIds', eventForm) || [];

  const filteredEventContacts = useMemo(() => {
    if (!selectedEventCompanyIds || selectedEventCompanyIds.length === 0) {
      return crmStore.contacts;
    }
    return crmStore.contacts.filter(c => {
      const compId = c.externalCompanyId ?? c.externalCompany?.id;
      return compId != null && selectedEventCompanyIds.includes(compId);
    });
  }, [crmStore.contacts, selectedEventCompanyIds]);

  // Auto-update generated name while typing in event form
  const handleEventValuesChange = (changedValues: any, all: any) => {
    if (changedValues.externalCompanyIds) {
      const companyIds: number[] = changedValues.externalCompanyIds || [];
      if (companyIds.length > 0) {
        const currentContactIds: number[] = all.contactIds || [];
        const validContactIds = currentContactIds.filter(id => {
          const c = crmStore.contacts.find(ct => ct.id === id);
          const compId = c?.externalCompanyId ?? c?.externalCompany?.id;
          return compId != null && companyIds.includes(compId);
        });
        if (validContactIds.length !== currentContactIds.length) {
          eventForm.setFieldsValue({ contactIds: validContactIds });
        }
      }
    }
    const composite = buildCompositeName(all.externalCompanyIds, all.contactIds, all.dateRange);
    if (composite && composite !== all.name) {
      eventForm.setFieldsValue({ name: composite });
    }
  };

  const saveEvent = async () => {
    try {
      const { dateRange, externalCompanyIds, contactIds, ...rest } = await eventForm.validateFields();
      
      const payload = {
        ...rest,
        dateStart: toApi(dateRange[0]),
        dateEnd: toApi(dateRange[1]),
        externalCompanyIds: externalCompanyIds || [],
        contactIds: contactIds || [],
        name: buildCompositeName(externalCompanyIds, contactIds, dateRange),
      };

      if (editingEvent) {
        await crmStore.updateCrmEvent(editingEvent.id, payload);
      } else {
        await crmStore.addCrmEvent(payload);
      }
      setEventModalVisible(false);
      refreshEvents();
      message.success(editingEvent ? t('SavedSuccessfully') : t('CreatedSuccessfully'));
    } catch (err) {
      console.log('Validation error:', err);
    }
  };

  const deleteEvent = async (id: number) => {
    await crmStore.deleteCrmEvent(id);
    refreshEvents();
    message.success(t('DeletedSuccessfully'));
  };

  /* ─────────────────────── column defs ─────────────────────── */

  const eventColumns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: '5%' },
    { title: t('Name'), dataIndex: 'name', key: 'name', width: '25%' },
    {
      title: t('Start'),
      dataIndex: 'dateStart',
      key: 'dateStart',
      width: '15%',
      render: (d: string) => dayjs(d).format('DD/MM/YYYY HH:mm'),
    },
    {
      title: t('End'),
      dataIndex: 'dateEnd',
      key: 'dateEnd',
      width: '15%',
      render: (d: string) => dayjs(d).format('DD/MM/YYYY HH:mm'),
    },
    {
      title: t('EventType'),
      dataIndex: 'typeId',
      key: 'typeId',
      width: '15%',
      render: (typeId: number) =>
        crmStore.CrmEventTypes.find((t) => t.id === typeId)?.name || '',
    },
    {
      title: t('Companies'),
      key: 'companies',
      width: '15%',
      render: (_: unknown, record: CrmEvent) => {
        const companies = record.externalCompanies || [];
        if (companies.length === 0) return '';
        if (companies.length === 1) return companies[0].name;
        return `${companies[0].name} +${companies.length - 1}`;
      },
    },
    {
      title: t('Actions'),
      key: 'actions',
      width: '10%',
      render: (_: unknown, record: CrmEvent) => (
        <Space>
          <Button icon={<EditOutlined />} onClick={() => openEditEvent(record)} />
          <Popconfirm
            title={t('DeleteEventConfirm')}
            onConfirm={() => deleteEvent(record.id)}
          >
            <Button danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  /* ─────────────────────── breadcrumbs ─────────────────────── */

  const breadcrumbItems = [
    { label: t('Home'), path: `/${applicationStore.company?.Tenant}` },
    { label: 'CRM', path: `/${applicationStore.company?.Tenant}/crm` },
    { label: t('Contacts'), path: `/${applicationStore.company?.Tenant}/crm/contacts` },
    {
      label: isCreateMode ? t('NewContact') : t('ContactDetails'),
      path: isCreateMode
        ? `/${applicationStore.company?.Tenant}/crm/contact/new`
        : `/${applicationStore.company?.Tenant}/crm/contact/${contactIdNumber}`,
    },
  ];

  /* ─────────────────────────── render ────────────────────────── */

  return (
    <ERPLayout breadcrumbTitle={t('ContactDetails')} breadcrumbItems={breadcrumbItems}>
      <Spin spinning={loading || crmStore.isLoading}>
        <Card bordered={false} className="crm-event-form-card">
          <div className="crm-detail-top-nav" style={{ marginBottom: 12, display: 'flex', alignItems: 'center' }}>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate(`/${applicationStore.company?.Tenant}/crm/contacts`)}
              className="crm-back-button"
            >
              {t('Back')}
            </Button>
          </div>
          {!isCreateMode && <CrmAuditInfo record={crmStore.currentContact} />}

          <Tabs activeKey={activeTab} onChange={setActiveTab} className="crm-modern-tabs">
            <TabPane tab={t('Details')} key="details">
              <div className="crm-event-editor-container">
                {/* Header title & subtitle */}
                <div className="crm-event-form-header">
                  <div className="crm-event-form-title">
                    {isCreateMode
                      ? t('crm.contactParameters', 'Параметри контакту')
                      : crmStore.currentContact
                      ? `${crmStore.currentContact.name} ${crmStore.currentContact.surname}`
                      : t('ContactDetails')}
                  </div>
                  <div className="crm-event-form-subtitle">
                    {isCreateMode
                      ? t('crm.contactSubtitle', 'Заповніть дані для збереження контакту CRM')
                      : t('crm.contactEditSubtitle', 'Перегляд та редагування даних контакту CRM')}
                  </div>
                </div>

                <Form form={form} layout="vertical" disabled={!editing} className="crm-event-modern-form">
                  {/* Group 1: General Information */}
                  <div className="crm-form-section-title">
                    {t('crm.generalInfoLabel', 'ОСНОВНА ІНФОРМАЦІЯ')}
                  </div>
                  <Row gutter={16}>
                    <Col xs={24} sm={12}>
                      <div className="crm-form-group">
                        <div className="crm-form-label-row">
                          <label className="crm-form-label">
                            {t('Name')} <span className="crm-required">*</span>
                          </label>
                        </div>
                        <Form.Item
                          name="name"
                          rules={[{ required: true, message: t('PleaseEnterName') }]}
                          style={{ marginBottom: 4 }}
                        >
                          <Input className="crm-modern-input" placeholder={t('Name')} />
                        </Form.Item>
                      </div>
                    </Col>
                    <Col xs={24} sm={12}>
                      <div className="crm-form-group">
                        <div className="crm-form-label-row">
                          <label className="crm-form-label">
                            {t('Surname')} <span className="crm-required">*</span>
                          </label>
                        </div>
                        <Form.Item
                          name="surname"
                          rules={[{ required: true, message: t('PleaseEnterSurname') }]}
                          style={{ marginBottom: 4 }}
                        >
                          <Input className="crm-modern-input" placeholder={t('Surname')} />
                        </Form.Item>
                      </div>
                    </Col>
                  </Row>

                  {/* Duplicate warning for Name / Surname */}
                  {editing && contactNameDuplicates?.exactMatch && (
                    <DuplicateWarning
                      type="error"
                      message={t('ContactWithNameExists')}
                      items={[
                        {
                          id: contactNameDuplicates.exactMatch.id,
                          title: `${contactNameDuplicates.exactMatch.name} ${contactNameDuplicates.exactMatch.surname}`,
                          details: [
                            contactNameDuplicates.exactMatch.externalCompany?.name
                              ? { label: t('Company'), value: contactNameDuplicates.exactMatch.externalCompany.name }
                              : null,
                            contactNameDuplicates.exactMatch.email
                              ? { label: t('Email'), value: contactNameDuplicates.exactMatch.email }
                              : null,
                            contactNameDuplicates.exactMatch.phoneNumber
                              ? { label: t('Phone'), value: contactNameDuplicates.exactMatch.phoneNumber }
                              : null,
                          ].filter(Boolean) as any[],
                          linkPath: `${tenantPath}/crm/contact/${contactNameDuplicates.exactMatch.id}`,
                        },
                      ]}
                    />
                  )}

                  {editing &&
                    !contactNameDuplicates?.exactMatch &&
                    (contactNameDuplicates?.similarMatches.length ?? 0) > 0 && (
                      <DuplicateWarning
                        type="warning"
                        message={t('SimilarContactsFound')}
                        items={contactNameDuplicates!.similarMatches.map((c) => ({
                          id: c.id,
                          title: `${c.name} ${c.surname}`,
                          details: [
                            c.externalCompany?.name
                              ? { label: t('Company'), value: c.externalCompany.name }
                              : null,
                            c.email ? { label: t('Email'), value: c.email } : null,
                            c.phoneNumber ? { label: t('Phone'), value: c.phoneNumber } : null,
                          ].filter(Boolean) as any[],
                          linkPath: `${tenantPath}/crm/contact/${c.id}`,
                        }))}
                      />
                    )}

                  <Row gutter={16}>
                    <Col xs={24} sm={12}>
                      <div className="crm-form-group">
                        <div className="crm-form-label-row">
                          <label className="crm-form-label">{t('Company')}</label>
                        </div>
                        <Form.Item name="externalCompanyId" style={{ marginBottom: 0 }}>
                          <Select
                            allowClear
                            placeholder={t('Select')}
                            className="crm-modern-select"
                            showSearch
                            optionFilterProp="children"
                          >
                            {crmStore.externalCompanies.map((c) => (
                              <Option key={c.id} value={c.id}>
                                {c.name}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </div>
                    </Col>
                    <Col xs={24} sm={12}>
                      <div className="crm-form-group">
                        <div className="crm-form-label-row">
                          <label className="crm-form-label">{t('Role')}</label>
                        </div>
                        <Form.Item name="roleId" style={{ marginBottom: 0 }}>
                          <Select
                            allowClear
                            placeholder={t('Select')}
                            className="crm-modern-select"
                            showSearch
                            optionFilterProp="children"
                          >
                            {crmStore.clientRoles.map((r) => (
                              <Option key={r.id} value={r.id}>
                                {r.name}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                      </div>
                    </Col>
                  </Row>

                  <div className="crm-form-section-divider" />

                  {/* Group 2: Contact Info */}
                  <div className="crm-form-section-title">
                    {t('crm.contactInfoLabel', 'ЗВ\'ЯЗОК ТА КОНТАКТНІ ДАНІ')}
                  </div>
                  <Row gutter={16}>
                    <Col xs={24} sm={12}>
                      <div className="crm-form-group">
                        <div className="crm-form-label-row">
                          <label className="crm-form-label">{t('Email')}</label>
                        </div>
                        <Form.Item
                          name="email"
                          rules={[{ type: 'email', message: t('InvalidEmailFormat') }]}
                          style={{ marginBottom: 4 }}
                        >
                          <Input type="email" className="crm-modern-input" placeholder="example@company.com" />
                        </Form.Item>
                      </div>
                      {editing && contactEmailDuplicate && (
                        <DuplicateWarning
                          type="error"
                          message={t('ContactWithEmailExists')}
                          items={[
                            {
                              id: contactEmailDuplicate.id,
                              title: `${contactEmailDuplicate.name} ${contactEmailDuplicate.surname}`,
                              details: [
                                contactEmailDuplicate.externalCompany?.name
                                  ? { label: t('Company'), value: contactEmailDuplicate.externalCompany.name }
                                  : null,
                                contactEmailDuplicate.email
                                  ? { label: t('Email'), value: contactEmailDuplicate.email }
                                  : null,
                              ].filter(Boolean) as any[],
                              linkPath: `${tenantPath}/crm/contact/${contactEmailDuplicate.id}`,
                            },
                          ]}
                        />
                      )}
                    </Col>
                    <Col xs={24} sm={12}>
                      <div className="crm-form-group">
                        <div className="crm-form-label-row">
                          <label className="crm-form-label">{t('Phone')}</label>
                        </div>
                        <Form.Item
                          name="phoneNumber"
                          rules={[
                            {
                              validator(_, value) {
                                if (!value) return Promise.resolve();
                                if (!isValidPhoneNumber(value)) {
                                  return Promise.reject(new Error(t('InvalidPhoneFormat')));
                                }
                                return Promise.resolve();
                              },
                            },
                          ]}
                          style={{ marginBottom: 4 }}
                        >
                          <Input className="crm-modern-input" placeholder="+380..." />
                        </Form.Item>
                      </div>
                      {editing && contactPhoneDuplicate && (
                        <DuplicateWarning
                          type="error"
                          message={t('ContactWithPhoneExists')}
                          items={[
                            {
                              id: contactPhoneDuplicate.id,
                              title: `${contactPhoneDuplicate.name} ${contactPhoneDuplicate.surname}`,
                              details: [
                                contactPhoneDuplicate.externalCompany?.name
                                  ? { label: t('Company'), value: contactPhoneDuplicate.externalCompany.name }
                                  : null,
                                contactPhoneDuplicate.phoneNumber
                                  ? { label: t('Phone'), value: contactPhoneDuplicate.phoneNumber }
                                  : null,
                              ].filter(Boolean) as any[],
                              linkPath: `${tenantPath}/crm/contact/${contactPhoneDuplicate.id}`,
                            },
                          ]}
                        />
                      )}
                    </Col>
                  </Row>

                  <div className="crm-form-section-divider" />

                  {/* Group 3: Additional Data */}
                  <div className="crm-form-section-title">
                    {t('crm.additionalInfoLabel', 'ДОДАТКОВІ ДАНІ')}
                  </div>
                  <Form.List name="additionalInfos">
                    {(fields, { add, remove }) => (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {fields.map(({ key, name, ...restField }) => {
                          const currentInfo = watchedContactAdditionalInfos?.[name];
                          const duplicateInfo =
                            editing && currentInfo?.type && currentInfo?.name
                              ? checkAdditionalInfoDuplicate(
                                  name,
                                  currentInfo.type,
                                  currentInfo.name,
                                  watchedContactAdditionalInfos,
                                  crmStore.contacts,
                                  crmStore.contactTypes,
                                  contactIdNumber,
                                  watchedContactPhone,
                                  watchedContactEmail,
                                )
                              : undefined;

                          return (
                            <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                              <div
                                style={{
                                  display: 'flex',
                                  gap: 12,
                                  alignItems: 'flex-start',
                                  width: '100%',
                                }}
                              >
                                <Form.Item
                                  {...restField}
                                  name={[name, 'type']}
                                  className="crm-no-explain"
                                  style={{ marginBottom: 0, width: 170, flexShrink: 0 }}
                                  rules={[{ required: true, message: t('PleaseSelectType') }]}
                                >
                                  <Select
                                    placeholder={t('Type')}
                                    allowClear
                                    showSearch
                                    className="crm-modern-select"
                                  >
                                    {contactTypeOptions.map((typeName) => (
                                      <Option key={typeName} value={typeName}>
                                        {typeName}
                                      </Option>
                                    ))}
                                  </Select>
                                </Form.Item>
                                <Form.Item
                                  {...restField}
                                  name={[name, 'name']}
                                  className="crm-no-explain"
                                  style={{ marginBottom: 0, flex: 1 }}
                                  rules={[
                                    { required: true, message: t('PleaseEnterValue') },
                                    ({ getFieldValue }) => ({
                                      validator(_, value) {
                                        if (!value) return Promise.resolve();
                                        const currentType = getFieldValue(['additionalInfos', name, 'type']);
                                        const typeCfg = crmStore.contactTypes.find(
                                          (ct) => ct.name?.toLowerCase().trim() === (currentType || '').toLowerCase().trim()
                                        );
                                        const kind = typeCfg?.validationKind;

                                        if (kind === ContactValidationKind.PHONE) {
                                          if (!isValidPhoneNumber(value)) {
                                            return Promise.reject(new Error(t('InvalidPhoneFormat')));
                                          }
                                        } else if (kind === ContactValidationKind.EMAIL) {
                                          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                                          if (!emailRegex.test(value.trim())) {
                                            return Promise.reject(new Error(t('InvalidEmailFormat')));
                                          }
                                        } else if (kind === ContactValidationKind.URL) {
                                          const urlRegex = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/i;
                                          if (!urlRegex.test(value.trim())) {
                                            return Promise.reject(new Error(t('InvalidUrlFormat')));
                                          }
                                        }

                                        return Promise.resolve();
                                      },
                                    }),
                                  ]}
                                >
                                  <Input className="crm-modern-input" placeholder={t('Value')} />
                                </Form.Item>
                                {editing && (
                                  <Button
                                    type="text"
                                    danger
                                    icon={<MinusCircleOutlined />}
                                    onClick={() => remove(name)}
                                    style={{ height: 42, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                  />
                                )}
                              </div>
                              <Form.Item
                                noStyle
                                shouldUpdate={(prev, cur) =>
                                  prev?.additionalInfos?.[name] !== cur?.additionalInfos?.[name]
                                }
                              >
                                {() => {
                                  const typeErr = form.getFieldError(['additionalInfos', name, 'type']);
                                  const valErr = form.getFieldError(['additionalInfos', name, 'name']);
                                  const err = typeErr?.[0] || valErr?.[0];
                                  if (!err) return null;
                                  return (
                                    <div className="crm-field-error-row">
                                      {err}
                                    </div>
                                  );
                                }}
                              </Form.Item>
                              {duplicateInfo && (
                                <DuplicateWarning
                                  type="warning"
                                  message={
                                    duplicateInfo.isInternalDuplicate
                                      ? t('DuplicateInCurrentContact')
                                      : t('AdditionalInfoAlreadyUsed')
                                  }
                                  items={
                                    duplicateInfo.externalContact
                                      ? [
                                          {
                                            id: duplicateInfo.externalContact.id,
                                            title: `${duplicateInfo.externalContact.name} ${duplicateInfo.externalContact.surname}`,
                                            details: [
                                              duplicateInfo.externalContact.externalCompany?.name
                                                ? { label: t('Company'), value: duplicateInfo.externalContact.externalCompany.name }
                                                : null,
                                              { label: currentInfo?.type || '', value: duplicateInfo.matchedValue || '' },
                                            ].filter(Boolean) as any[],
                                            linkPath: `${tenantPath}/crm/contact/${duplicateInfo.externalContact.id}`,
                                          },
                                        ]
                                      : []
                                  }
                                />
                              )}
                            </div>
                          );
                        })}
                        {editing && (
                          <Button
                            type="dashed"
                            onClick={() => add()}
                            icon={<PlusCircleOutlined />}
                            style={{ height: 40, borderRadius: 8, marginTop: 4 }}
                          >
                            {t('AddField')}
                          </Button>
                        )}
                      </div>
                    )}
                  </Form.List>

                  {/* Form Actions in footer */}
                  <div className="crm-event-form-actions">
                    <div className="crm-event-actions-left">
                      {!isCreateMode && (
                        <Popconfirm title={t('DeleteContactConfirm')} onConfirm={onDelete}>
                          <Button danger icon={<DeleteOutlined />} className="crm-form-delete-btn">
                            {t('Delete')}
                          </Button>
                        </Popconfirm>
                      )}
                      {!isCreateMode && isGoogleContactsEnabled && (
                        <Tooltip
                          title={
                            isPhoneValidForGoogle
                              ? t('SyncWithGoogle', 'Синхронізувати з Google')
                              : t('InvalidPhoneForGoogle', 'Для синхронізації потрібен коректний номер телефону')
                          }
                        >
                          <span>
                            <Button
                              className="crm-google-sync-btn"
                              icon={<GoogleOutlined style={{ color: isPhoneValidForGoogle ? '#4285F4' : '#cbd5e1' }} />}
                              disabled={!isPhoneValidForGoogle || isSyncingSingleGoogle || crmStore.isGoogleSyncing}
                              loading={isSyncingSingleGoogle || crmStore.isGoogleSyncing}
                              onClick={handleSyncThisContact}
                              style={{ marginLeft: 8 }}
                            >
                              {t('SyncWithGoogle', 'Sync with Google')}
                            </Button>
                          </span>
                        </Tooltip>
                      )}
                    </div>

                    <div className="crm-event-actions-right">
                      {editing && !isCreateMode ? (
                        <Button className="crm-form-cancel-btn" onClick={handleCancel}>
                          {t('Cancel')}
                        </Button>
                      ) : (
                        <Button
                          className="crm-form-cancel-btn"
                          onClick={() => navigate(`/${applicationStore.company?.Tenant}/crm/contacts`)}
                        >
                          {t('Cancel')}
                        </Button>
                      )}

                      {editing ? (
                        <Button
                          type="primary"
                          className="crm-form-submit-btn"
                          icon={<CheckOutlined />}
                          onClick={onSave}
                          disabled={hasExactDuplicate}
                          title={hasExactDuplicate ? t('DuplicateContactExists', 'Контакт с такими данными уже существует') : undefined}
                        >
                          {isCreateMode ? t('crm.createContactBtn', 'Створити контакт') : t('crm.saveChangesBtn', 'Зберегти зміни')}
                        </Button>
                      ) : (
                        <Button
                          type="primary"
                          className="crm-form-submit-btn"
                          icon={<EditOutlined />}
                          onClick={() => setEditing(true)}
                        >
                          {t('Edit')}
                        </Button>
                      )}
                    </div>
                  </div>
                </Form>
              </div>
            </TabPane>

            {/* Events tab only for existing contacts */}
            {!isCreateMode && (
              <TabPane tab={t('Events')} key="events">
                <div style={{ paddingTop: 16 }}>
                  <div style={{ marginBottom: 16 }}>
                    <Button type="primary" icon={<PlusOutlined />} onClick={openAddEvent}>
                      {t('AddEvent')}
                    </Button>
                  </div>

                  <Table
                    dataSource={relatedEvents}
                    columns={eventColumns}
                    rowKey="id"
                    pagination={{ pageSize: 10 }}
                    expandable={{
                      expandedRowRender: (record) => (
                        <div style={{ padding: 16 }}>
                          <Text strong>{t('Description')}:</Text>
                          <Typography.Paragraph>
                            {record.description || t('NoDescription')}
                          </Typography.Paragraph>
                        </div>
                      ),
                    }}
                  />
                </div>
              </TabPane>
            )}

            {!isCreateMode && contactIdNumber !== null && (
              <TabPane tab={t('Files')} key="files">
                <CrmAttachmentsTab entityType="Contact" entityId={contactIdNumber} active={activeTab === 'files'} />
              </TabPane>
            )}

            {!isCreateMode && contactIdNumber !== null && (
              <TabPane tab={t('History')} key="history">
                <CrmHistoryTab entityType="Contact" entityId={contactIdNumber} active={activeTab === 'history'} />
              </TabPane>
            )}
          </Tabs>
        </Card>

        {/* ─────────── Modal: Add / Edit event - FULL FORM MATCHING EventDetail ─────────── */}
        <Modal
          title={
            <div style={{ fontSize: 18, fontWeight: 700, color: '#0f172a', paddingBottom: 4 }}>
              {editingEvent ? t('EditEvent') : t('AddEvent')}
            </div>
          }
          open={eventModalVisible}
          onCancel={() => setEventModalVisible(false)}
          width={840}
          destroyOnClose
          centered
          footer={[
            <Button
              key="cancel"
              onClick={() => setEventModalVisible(false)}
              className="crm-form-cancel-btn"
              style={{ minWidth: 100, height: 38 }}
            >
              {t('Cancel')}
            </Button>,
            <Button
              key="submit"
              type="primary"
              onClick={saveEvent}
              className="crm-form-submit-btn"
              style={{ minWidth: 140, height: 38, background: '#3b82f6' }}
            >
              {editingEvent ? t('crm.saveChanges', 'Зберегти зміни') : t('crm.createEvent', 'Створити подію')}
            </Button>,
          ]}
        >
          <Form
            form={eventForm}
            layout="vertical"
            onValuesChange={handleEventValuesChange}
          >
            <Row gutter={24}>
              <Col xs={24} md={12}>
                <div className="crm-form-section-title" style={{ marginTop: 4 }}>
                  {t('crm.generalInfoLabel', 'ОСНОВНІ ДАНІ')}
                </div>

                <div className="crm-form-group">
                  <div className="crm-form-label-row">
                    <label className="crm-form-label">
                      {t('Companies')} <span className="crm-required">*</span>
                    </label>
                  </div>
                  <Form.Item
                    name="externalCompanyIds"
                    rules={[
                      {
                        validator(_, value) {
                          if (!value || value.length === 0) {
                            return Promise.reject(new Error(t('PleaseSelectCompany')));
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                    style={{ marginBottom: 8 }}
                  >
                    <FormCheckboxDropdown
                      options={crmStore.externalCompanies}
                      placeholder={t('crm.chooseCompanyPlaceholder', '+ Оберіть або введіть компанію...')}
                      searchPlaceholder={t('Search')}
                    />
                  </Form.Item>
                </div>

                <div className="crm-form-group">
                  <div className="crm-form-label-row">
                    <label className="crm-form-label">{t('Contacts')}</label>
                  </div>
                  <Form.Item name="contactIds" style={{ marginBottom: 8 }}>
                    <FormCheckboxDropdown
                      options={filteredEventContacts.map((c) => ({ id: c.id, name: `${c.name} ${c.surname}`.trim() }))}
                      placeholder={t('crm.chooseContactPlaceholder', '+ Додати контакт...')}
                      searchPlaceholder={t('Search')}
                    />
                  </Form.Item>
                </div>

                <div className="crm-form-group">
                  <div className="crm-form-label-row">
                    <label className="crm-form-label">
                      {t('EventType')} <span className="crm-required">*</span>
                    </label>
                  </div>
                  <Form.Item
                    name="typeId"
                    rules={[{ required: true, message: t('PleaseSelectEventType') }]}
                    style={{ marginBottom: 8 }}
                  >
                    <Select placeholder={t('Select')} allowClear className="crm-modern-select">
                      {crmStore.CrmEventTypes.map((type) => (
                        <Option key={type.id} value={type.id}>
                          {type.name}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </div>

                <div className="crm-form-group">
                  <div className="crm-form-label-row">
                    <label className="crm-form-label">
                      {t('DateTime')} <span className="crm-required">*</span>
                    </label>
                  </div>
                  <Form.Item
                    name="dateRange"
                    rules={[{ required: true, message: t('PleaseSelectDateTime') }]}
                    style={{ marginBottom: 4 }}
                  >
                    <RangePicker
                      showTime
                      allowClear={false}
                      format="DD/MM/YYYY HH:mm"
                      style={{ width: '100%', height: 38, borderRadius: 8 }}
                    />
                  </Form.Item>
                </div>
              </Col>

              <Col xs={24} md={12}>
                <div className="crm-form-section-title" style={{ marginTop: 4 }}>
                  {t('crm.eventDetailsLabel', 'ДЕТАЛІ ПОДІЇ')}
                </div>

                <div className="crm-form-group">
                  <div className="crm-form-label-row">
                    <label className="crm-form-label">
                      {t('Name')} <span className="crm-required">*</span>
                    </label>
                  </div>
                  <Form.Item
                    name="name"
                    rules={[{ required: true }]}
                    style={{ marginBottom: 8 }}
                  >
                    <Input disabled className="crm-modern-input" style={{ background: '#f8fafc' }} />
                  </Form.Item>
                </div>

                <div className="crm-form-group">
                  <div className="crm-form-label-row">
                    <label className="crm-form-label">{t('Description')}</label>
                  </div>
                  <Form.Item name="description" style={{ marginBottom: 4 }}>
                    <TextArea
                      rows={6}
                      placeholder={t('Description')}
                      style={{ borderRadius: 8, borderColor: '#e2e8f0', resize: 'vertical' }}
                    />
                  </Form.Item>
                </div>
              </Col>
            </Row>
          </Form>
        </Modal>
      </Spin>
      <GoogleSyncToast state={syncToast} onClose={() => setSyncToast(prev => ({ ...prev, visible: false }))} />
    </ERPLayout>
  );
});

export default ContactDetail;